import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import {
  Loader2, ClipboardList, CheckCircle2, XCircle, TrendingUp, TrendingDown,
  Minus, AlertTriangle, Clock, Sparkles, Timer, BarChart3, ChevronRight, Trash2,
  Download, User, ArrowLeft, Star, Camera, X, FileText, Calendar, Send, Save, Eye,
  MapPin
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { SnapshotBadge } from '../../components/checklists/SnapshotBadge';
import { FotoGrid } from '../../components/checklists/ChecklistFormElements';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }) : '-';
const scoreBg = (p) => {
  if (p == null) return 'bg-slate-100 text-slate-500';
  if (p >= 85) return 'bg-green-100 text-green-700';
  if (p >= 70) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};
const scoreColor = (p) => {
  if (p == null) return 'text-slate-400';
  if (p >= 85) return 'text-green-600';
  if (p >= 70) return 'text-amber-600';
  return 'text-red-600';
};

// ── AI Modal ────────────────────────────────────────────────────────────────
const AiModal = ({ open, onClose, checklistId, locatieNaam }) => {
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !checklistId) return;
    setLoading(true);
    setAnalyse(null);
    setError(null);
    fetch(`${API_V2}/checklists/${checklistId}/ai-analyse`, { method: 'POST', headers: hdrs() })
      .then(r => {
        if (!r.ok) throw new Error('AI analyse mislukt');
        return r.json();
      })
      .then(d => setAnalyse(d))
      .catch(e => { setError(e.message); toast.error('AI analyse mislukt'); })
      .finally(() => setLoading(false));
  }, [open, checklistId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>AI Analyse — {locatieNaam}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-2" />
            <span className="text-slate-500">Analyse genereren...</span>
          </div>
        ) : analyse?.tekst ? (
          <div className="bg-purple-50 rounded-lg p-4 space-y-2" data-testid="ai-modal-result">
            <p className="text-sm text-purple-900 whitespace-pre-wrap">{analyse.tekst}</p>
            <p className="text-xs text-purple-400 mt-2">{analyse.model} — {fmtDate(analyse.gegenereerd_op)}</p>
          </div>
        ) : (
          <div className="py-8 text-center text-slate-400">{error || 'Geen analyse beschikbaar'}</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Tab: Overzicht ──────────────────────────────────────────────────────────
const TabOverzicht = ({ data, onAi }) => {
  if (!data) return null;
  const totOpen = data.reduce((s, l) => s + l.open, 0);
  const totVerlopen = data.reduce((s, l) => s + l.verlopen, 0);
  const totIngevuld = data.reduce((s, l) => s + l.ingevuld, 0);
  const scores = data.filter(l => l.gemiddelde_score != null).map(l => l.gemiddelde_score);
  const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10 : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Open</p>
          <p className="text-2xl font-bold text-blue-600" data-testid="stat-open">{totOpen}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Verlopen</p>
          <p className="text-2xl font-bold text-red-600" data-testid="stat-verlopen">{totVerlopen}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Ingevuld</p>
          <p className="text-2xl font-bold text-green-600" data-testid="stat-ingevuld">{totIngevuld}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Gem. score</p>
          <p className={`text-2xl font-bold ${scoreColor(avg)}`} data-testid="stat-avg">{avg != null ? `${avg}%` : '-'}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="overzicht-table">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Open</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Verlopen</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Ingevuld</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Gem. score</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">AI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.map(loc => (
                <tr key={loc.locatie_id} className="hover:bg-slate-50" data-testid={`row-${loc.locatie_naam}`}>
                  <td className="py-3 px-4 font-medium text-slate-700">{loc.locatie_naam}</td>
                  <td className="py-3 px-4 text-center">
                    {loc.open > 0 ? <Badge className="bg-blue-100 text-blue-700">{loc.open}</Badge> : <span className="text-slate-300">0</span>}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {loc.verlopen > 0 ? <Badge className="bg-red-100 text-red-700">{loc.verlopen}</Badge> : <span className="text-slate-300">0</span>}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {loc.ingevuld > 0 ? <Badge className="bg-green-100 text-green-700">{loc.ingevuld}</Badge> : <span className="text-slate-300">0</span>}
                  </td>
                  <td className="py-3 px-4">
                    {loc.gemiddelde_score != null
                      ? <Badge className={scoreBg(loc.gemiddelde_score)}>{loc.gemiddelde_score}%</Badge>
                      : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {loc.laatste_checklist_id ? (
                      <button onClick={() => onAi(loc.laatste_checklist_id, loc.locatie_naam)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                        data-testid={`ai-btn-${loc.locatie_naam}`}>
                        <Sparkles className="w-3 h-3" /> Analyse
                      </button>
                    ) : <span className="text-slate-300">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Verlopen (with bulk select) ────────────────────────────────────────
const TabVerlopen = ({ data, onDelete, onBulkDelete }) => {
  const [selected, setSelected] = useState(new Set());

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
        <p className="text-slate-500">Geen verlopen checklists</p>
      </div>
    );
  }

  const toggleSel = (id) => setSelected(prev => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const toggleAll = () => setSelected(prev => prev.size === data.length ? new Set() : new Set(data.map(c => c.id)));

  return (
    <div className="space-y-3">
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
          <span className="text-sm text-red-700 font-medium">{selected.size} geselecteerd</span>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => onBulkDelete([...selected])} data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijder geselecteerde ({selected.size})
          </Button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="verlopen-table">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-3 w-10"><Checkbox checked={selected.size === data.length && data.length > 0} onCheckedChange={toggleAll} data-testid="select-all-verlopen" /></th>
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Template</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Toegewezen aan</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Deadline</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Dagen over</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.map(cl => {
                const dl = cl.periode?.deadline;
                const dagenOver = dl ? Math.floor((Date.now() - new Date(dl).getTime()) / 86400000) : 0;
                return (
                  <tr key={cl.id} className="hover:bg-slate-50" data-testid={`verlopen-${cl.id}`}>
                    <td className="py-3 px-3"><Checkbox checked={selected.has(cl.id)} onCheckedChange={() => toggleSel(cl.id)} /></td>
                    <td className="py-3 px-4 font-medium text-slate-700">{cl.locatie_naam}</td>
                    <td className="py-3 px-4 text-slate-600">{cl.template_naam}</td>
                    <td className="py-3 px-4 text-slate-500 text-xs">
                      {cl.invulling_modus === 'persoon' ? (cl.toegewezen_aan_naam || cl.ingevuld_door_naam || '-') : <span className="text-slate-300">Locatie-breed</span>}
                    </td>
                    <td className="py-3 px-4 text-slate-500">{fmtDateShort(dl)}</td>
                    <td className="py-3 px-4"><Badge className="bg-red-100 text-red-700">{dagenOver}d</Badge></td>
                    <td className="py-3 px-2">
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                        onClick={() => onDelete(cl)} data-testid={`delete-checklist-${cl.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Geëscaleerd (with bulk select) ─────────────────────────────────────
const TabGeescaleerd = ({ data, onDelete, onBulkDelete }) => {
  const [selected, setSelected] = useState(new Set());

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
        <p className="text-slate-500">Geen geëscaleerde checklists</p>
      </div>
    );
  }

  const toggleSel = (id) => setSelected(prev => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const toggleAll = () => setSelected(prev => prev.size === data.length ? new Set() : new Set(data.map(c => c.id)));

  return (
    <div className="space-y-3">
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
          <span className="text-sm text-red-700 font-medium">{selected.size} geselecteerd</span>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => onBulkDelete([...selected])} data-testid="bulk-delete-geescaleerd-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijder geselecteerde ({selected.size})
          </Button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="geescaleerd-table">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-3 w-10"><Checkbox checked={selected.size === data.length && data.length > 0} onCheckedChange={toggleAll} data-testid="select-all-geescaleerd" /></th>
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Template</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Toegewezen aan</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Deadline</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Escalatie</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.map(cl => (
                <tr key={cl.id} className="hover:bg-red-50" data-testid={`geescaleerd-${cl.id}`}>
                  <td className="py-3 px-3"><Checkbox checked={selected.has(cl.id)} onCheckedChange={() => toggleSel(cl.id)} /></td>
                  <td className="py-3 px-4 font-medium text-slate-700">{cl.locatie_naam}</td>
                  <td className="py-3 px-4 text-slate-600">{cl.template_naam}</td>
                  <td className="py-3 px-4 text-slate-500 text-xs">
                    {cl.invulling_modus === 'persoon' ? (cl.toegewezen_aan_naam || cl.ingevuld_door_naam || '-') : <span className="text-slate-300">Locatie-breed</span>}
                  </td>
                  <td className="py-3 px-4 text-slate-500">{fmtDateShort(cl.periode?.deadline)}</td>
                  <td className="py-3 px-4">
                    <Badge className="bg-red-500 text-white">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {cl.escalatie_dagen_over}d over escalatie
                    </Badge>
                  </td>
                  <td className="py-3 px-2">
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                      onClick={() => onDelete(cl)} data-testid={`delete-checklist-${cl.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Tijden ─────────────────────────────────────────────────────────────
const TabTijden = ({ data }) => {
  // v18.4: backend levert { rijen, statistiek } — mediaan en gemiddelde zonder
  // uitschieters, want één checklist die 29 dagen openstond vertekende het gemiddelde.
  const rijen = data?.rijen || [];
  const stat = data?.statistiek || {};
  if (rijen.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <Timer className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-500">Nog geen invultijden beschikbaar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Mediaan invultijd</p>
          <p className="text-2xl font-bold text-[#1f2548]" data-testid="median-time">
            {stat.mediaan_minuten != null ? `${stat.mediaan_minuten} min` : '-'}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Gemiddeld zonder uitschieters (&gt; 8 uur)</p>
          <p className="text-2xl font-bold text-[#1f2548]" data-testid="avg-time">
            {stat.gemiddeld_zonder_uitschieters_minuten != null ? `${stat.gemiddeld_zonder_uitschieters_minuten} min` : '-'}
          </p>
          {stat.uitschieters > 0 && (
            <p className="text-xs text-slate-400 mt-1" data-testid="uitschieters-info">
              {stat.uitschieters} uitschieter{stat.uitschieters === 1 ? '' : 's'} niet meegerekend
            </p>
          )}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tijden-table">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Template</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Ingevuld door</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Duur</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Datum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rijen.map((t, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-700">{t.locatie_naam}</td>
                  <td className="py-3 px-4 text-slate-600">{t.template_naam}</td>
                  <td className="py-3 px-4 text-slate-500">{t.ingevuld_door_naam || '-'}</td>
                  <td className="py-3 px-4">
                    <Badge className={t.duur_minuten > 30 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                      {t.duur_minuten ? `${t.duur_minuten} min` : '-'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-slate-500">{fmtDateShort(t.ingevuld_op)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Tab: Scores ─────────────────────────────────────────────────────────────
const TabScores = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-500">Nog geen score data beschikbaar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map(loc => {
        const scores = loc.scores || [];
        const avg = scores.length ? Math.round(scores.reduce((s, sc) => s + (sc.percentage || 0), 0) / scores.length * 10) / 10 : null;
        const trend = scores.length >= 2
          ? (scores[scores.length - 1].percentage || 0) - (scores[scores.length - 2].percentage || 0)
          : 0;
        return (
          <div key={loc.locatie_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`scores-${loc.locatie_naam}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800">{loc.locatie_naam}</h3>
              <div className="flex items-center gap-2">
                {avg != null && <Badge className={scoreBg(avg)}>Gem. {avg}%</Badge>}
                {trend > 0 && <TrendingUp className="w-4 h-4 text-green-500" />}
                {trend < 0 && <TrendingDown className="w-4 h-4 text-red-500" />}
                {trend === 0 && scores.length >= 2 && <Minus className="w-4 h-4 text-slate-400" />}
              </div>
            </div>
            <div className="flex gap-1 items-end h-16">
              {scores.map((sc, i) => {
                const pct = sc.percentage || 0;
                const h = Math.max(8, (pct / 100) * 60);
                const bg = pct >= 85 ? 'bg-green-400' : pct >= 70 ? 'bg-amber-400' : 'bg-red-400';
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative" title={`${fmtDateShort(sc.datum)}: ${pct}%`}>
                    <div className={`w-full rounded-t ${bg} transition-all`} style={{ height: `${h}px` }} />
                    <span className="text-[9px] text-slate-400 hidden sm:block">{fmtDateShort(sc.datum)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Management Detail View (read-only) ──────────────────────────────────────
const fmtDateTime = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const ManagementDetailView = ({ checklistId, onBack }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [aiAnalyse, setAiAnalyse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [uitgebreid, setUitgebreid] = useState(null);
  const [uitgebreidLoading, setUitgebreidLoading] = useState(false);

  useEffect(() => {
    if (!checklistId) return;
    setLoading(true);
    fetch(`${API_V2}/checklists/${checklistId}`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setData(d); if (d.ai_analyse?.tekst) setAiAnalyse(d.ai_analyse); } })
      .catch(() => toast.error('Laden mislukt'))
      .finally(() => setLoading(false));
  }, [checklistId]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const t = getAccessToken();
      const res = await fetch(`${API_V2}/checklists/${checklistId}/export-pdf`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const disp = res.headers.get('content-disposition');
        const fn = disp?.match(/filename="(.+)"/)?.[1] || 'checklist.pdf';
        a.href = url; a.download = fn; a.click(); URL.revokeObjectURL(url);
        toast.success('PDF gedownload');
      } else toast.error('PDF export mislukt');
    } catch { toast.error('PDF export mislukt'); }
    finally { setExporting(false); }
  };

  const handleAiAnalyse = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_V2}/checklists/${checklistId}/ai-analyse`, { method: 'POST', headers: hdrs() });
      if (res.ok) { const d = await res.json(); setAiAnalyse(d); }
      else toast.error('AI analyse mislukt');
    } catch { toast.error('AI analyse mislukt'); }
    finally { setAiLoading(false); }
  };

  const fetchUitgebreid = async (force = false) => {
    setUitgebreidLoading(true);
    try {
      const url = `${API_V2}/checklists/${checklistId}/uitgebreide-analyse${force ? '?force=true' : ''}`;
      const res = await fetch(url, { method: 'POST', headers: hdrs() });
      if (res.ok) {
        const d = await res.json();
        setUitgebreid(d);
      } else {
        toast.error('Uitgebreide analyse mislukt');
      }
    } catch {
      toast.error('Uitgebreide analyse mislukt');
    } finally {
      setUitgebreidLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-400">Checklist niet gevonden</div>;

  const tmpl = data.template || {};
  const items = data.items || [];
  const secties = tmpl.secties || [];
  const schaal = tmpl.beoordeling_schaal || 10;
  const saMap = {};
  (data.sectie_antwoorden || []).forEach(sa => { saMap[sa.sectie_id] = sa; });

  const itemsBySectie = {};
  items.forEach(it => {
    const key = it.sectie_id || '__none__';
    if (!itemsBySectie[key]) itemsBySectie[key] = [];
    itemsBySectie[key].push(it);
  });

  const renderItemValue = (item) => {
    if (item.type === 'checkbox') {
      return item.waarde === true
        ? <Badge className="bg-green-100 text-green-700 text-xs">Ja</Badge>
        : item.waarde === false
          ? <Badge className="bg-red-100 text-red-700 text-xs">Nee</Badge>
          : <span className="text-sm text-slate-400">-</span>;
    }
    if (item.type === 'beoordeling') {
      const val = item.waarde || 0;
      return (
        <div className="flex items-center gap-1 flex-wrap">
          {Array.from({ length: schaal }).map((_, i) => (
            <Star key={i} className={`w-4 h-4 ${i < val ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
          ))}
          <span className="text-xs font-semibold text-slate-500 ml-1">{val}/{schaal}</span>
        </div>
      );
    }
    // Text items - show full text
    return item.waarde
      ? <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2 whitespace-pre-wrap break-words">{item.waarde}</p>
      : <span className="text-sm text-slate-400">-</span>;
  };

  return (
    <div className="space-y-4 max-w-3xl mx-auto px-4 sm:px-0 pb-12" data-testid="management-detail-view">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg mt-0.5" data-testid="detail-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-[#1f2548]">{data.template_naam}</h1>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {data.locatie_naam}</span>
            {data.invulling_modus === 'persoon' && (data.toegewezen_aan_naam || data.ingevuld_door_naam) && (
              <span className="text-sm text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Voor: {data.toegewezen_aan_naam || data.ingevuld_door_naam}</span>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exporting} data-testid="detail-export-pdf-btn">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
          PDF
        </Button>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="detail-summary">
        {data.score && (
          <div><p className="text-[10px] uppercase text-slate-400 font-medium">Score</p>
            <span className={`text-xl font-bold ${scoreColor(data.score.percentage)}`}>{data.score.percentage}%</span>
            <span className="text-xs text-slate-400 ml-1">{data.score.goed_items}/{data.score.totaal_checkbox_items}</span></div>
        )}
        <div><p className="text-[10px] uppercase text-slate-400 font-medium">Ingevuld door</p>
          <p className="text-sm font-medium text-slate-700">{data.ingevuld_door_naam || '-'}</p></div>
        <div><p className="text-[10px] uppercase text-slate-400 font-medium">Datum</p>
          <p className="text-sm text-slate-700">{fmtDateTime(data.ingevuld_op)}</p></div>
        {data.tijdregistratie?.duur_seconden && (
          <div><p className="text-[10px] uppercase text-slate-400 font-medium">Invultijd</p>
            <p className="text-sm text-slate-700">{Math.round(data.tijdregistratie.duur_seconden / 60)} min</p></div>
        )}
        <div><p className="text-[10px] uppercase text-slate-400 font-medium">Status</p>
          <Badge className={data.te_laat ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
            {data.te_laat ? 'Te laat' : 'Op tijd'}
          </Badge></div>
      </div>

      {/* Sections with items — naam BOVEN antwoord */}
      <SnapshotBadge
        template={tmpl}
        snapshotDate={data.template_snapshot?.snapshot_genomen_op}
        fallbackDate={data.ingevuld_op}
      />
      {secties.map(sectie => {
        const sItems = itemsBySectie[sectie.id || sectie._id] || [];
        const sa = saMap[sectie.id || sectie._id] || {};
        return (
          <div key={sectie.id || sectie._id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h3 className="font-semibold text-[#1f2548] text-sm sm:text-base">{sectie.naam}</h3>
            </div>
            <div className="divide-y divide-slate-100 px-4">
              {sItems.map((item, i) => (
                <div key={i} className="py-3 space-y-1.5" data-testid={`detail-item-${item.item_id}`}>
                  <p className="text-sm font-medium text-slate-700">{item.naam}</p>
                  <div>{renderItemValue(item)}</div>
                  {item.foto_urls?.length > 0 && (
                    <div className="flex gap-2 flex-wrap mt-1">
                      <FotoGrid urls={item.foto_urls} thumbClass="w-16 h-16 sm:w-20 sm:h-20" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Section fields */}
            {(sa.toelichting || sa.actiepunten || sa.foto_urls?.length > 0) && (
              <div className="px-4 py-3 bg-slate-50/50 border-t border-slate-100 space-y-2">
                {sa.toelichting && <div><p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Toelichting</p><p className="text-sm text-slate-600 whitespace-pre-wrap">{sa.toelichting}</p></div>}
                {sa.actiepunten && <div><p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Actiepunten</p><p className="text-sm text-slate-600 whitespace-pre-wrap">{sa.actiepunten}</p></div>}
                {sa.foto_urls?.length > 0 && (
                  <div className="flex gap-2 flex-wrap"><FotoGrid urls={sa.foto_urls} thumbClass="w-20 h-20" /></div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Items without section */}
      {(itemsBySectie['__none__'] || []).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-semibold text-[#1f2548] text-sm">Overige items</h3>
          </div>
          <div className="divide-y divide-slate-100 px-4">
            {(itemsBySectie['__none__'] || []).map((item, i) => (
              <div key={i} className="py-3 space-y-1.5" data-testid={`detail-item-${item.item_id}`}>
                <p className="text-sm font-medium text-slate-700">{item.naam}</p>
                <div>{renderItemValue(item)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI analyse */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ai-analyse-section">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <h3 className="font-semibold text-[#1f2548] text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Analyse</h3>
          {!aiAnalyse?.tekst && (
            <Button size="sm" variant="outline" onClick={handleAiAnalyse} disabled={aiLoading} data-testid="detail-ai-btn">
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Genereer
            </Button>
          )}
        </div>
        <div className="p-4">
          {aiLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Analyse genereren...</div>
          ) : aiAnalyse?.tekst ? (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiAnalyse.tekst}</p>
          ) : (
            <p className="text-sm text-slate-400 text-center py-2">Nog geen analyse beschikbaar</p>
          )}
        </div>
      </div>

      {/* Uitgebreide analyse — admin only, met historische context */}
      {user?.rol === 'admin' && (
        <div
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          data-testid="uitgebreide-analyse-section"
        >
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-[#1f2548] text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Uitgebreide analyse
              <span className="text-xs font-normal text-slate-400">(admin)</span>
            </h3>
            <div className="flex gap-2">
              {!uitgebreid && !uitgebreidLoading && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchUitgebreid(false)}
                  data-testid="btn-uitgebreide-analyse"
                >
                  <BarChart3 className="w-3 h-3 mr-1" /> Analyse genereren
                </Button>
              )}
              {uitgebreid && !uitgebreidLoading && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchUitgebreid(true)}
                  data-testid="btn-uitgebreide-opnieuw"
                >
                  <Loader2 className="w-3 h-3 mr-1" /> Opnieuw genereren
                </Button>
              )}
            </div>
          </div>
          <div className="p-4">
            {uitgebreidLoading ? (
              <div className="flex items-center gap-2 text-slate-400 py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Uitgebreide analyse wordt gegenereerd...
              </div>
            ) : uitgebreid?.analyse ? (
              <>
                <div
                  className="prose prose-sm max-w-none text-slate-700"
                  data-testid="uitgebreide-analyse-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(uitgebreid.analyse)) }}
                />
                <div className="mt-3 text-xs text-slate-500 border-t pt-2">
                  Gebaseerd op {uitgebreid.context?.aantal_historie ?? 0} eerdere invullingen
                  {uitgebreid.context?.performance_beschikbaar
                    ? ' en performance data van afgelopen week.'
                    : ' (geen performance data beschikbaar voor deze locatie).'}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400 text-center py-2">
                Klik op "Analyse genereren" voor een uitgebreide analyse met historische context en performance data.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tab: Ingevuld (with detail click-through + person filter) ───────────────
const PAGINA = 100;

const TabIngevuld = ({ onViewDetail }) => {
  const [data, setData] = useState([]);
  const [totaal, setTotaal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meerBezig, setMeerBezig] = useState(false);
  const [personFilter, setPersonFilter] = useState('alle');
  const [persons, setPersons] = useState([]);
  const verzoekRef = React.useRef(0);   // F15: negeer verouderde antwoorden

  // E8: serverzijdig filteren en paginering — de browser filtert niet meer zelf
  // over een afgekapte lijst.
  const haal = React.useCallback(async (skip, stil) => {
    if (stil) setMeerBezig(true); else setLoading(true);
    try {
      const beurt = ++verzoekRef.current;
      const qs = new URLSearchParams({ skip: String(skip), limit: String(PAGINA) });
      if (personFilter !== 'alle') qs.set('persoon', personFilter);
      const res = await fetch(`${API_V2}/checklists/management/ingevuld?${qs}`, { headers: hdrs() });
      if (!res.ok) throw new Error('laden mislukt');
      const d = await res.json();
      const rijen = Array.isArray(d) ? d : (d.items || []);
      // F15: een ouder antwoord mag een nieuwer antwoord niet overschrijven
      if (beurt !== verzoekRef.current) return;
      setTotaal(Number(res.headers.get('X-Totaal') || rijen.length + skip));
      setData(prev => {
        if (!skip) return rijen;
        const gezien = new Set(prev.map(r => r.id));
        return [...prev, ...rijen.filter(r => !gezien.has(r.id))];
      });
      try {
        const vanServer = JSON.parse(res.headers.get('X-Personen') || 'null');
        if (Array.isArray(vanServer) && personFilter === 'alle') setPersons(vanServer);
      } catch { /* header ontbreekt (oude backend) — lijst laten staan */ }
    } catch {
      toast.error('Laden mislukt');
    } finally {
      setMeerBezig(false);
      setLoading(false);
    }
  }, [personFilter]);

  useEffect(() => { haal(0, false); }, [haal]);

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#1f2548]" /></div>;

  const filtered = data;

  if (!data.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-500">Nog geen ingevulde checklists</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Person filter */}
      {persons.length > 0 && (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-400" />
          <Select value={personFilter} onValueChange={setPersonFilter}>
            <SelectTrigger className="w-52 h-8 text-sm" data-testid="person-filter">
              <SelectValue placeholder="Filter op persoon..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle personen</SelectItem>
              {persons.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400" data-testid="ingevuld-teller">
            {filtered.length} van {totaal}
          </span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ingevuld-table">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Template</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Toegewezen aan</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Ingevuld door</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Score</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Datum</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(cl => (
                <tr key={cl.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onViewDetail(cl.id)} data-testid={`ingevuld-${cl.id}`}>
                  <td className="py-3 px-4 font-medium text-slate-700">{cl.locatie_naam}</td>
                  <td className="py-3 px-4 text-slate-600">{cl.template_naam}</td>
                  <td className="py-3 px-4 text-slate-500 text-xs">
                    {cl.invulling_modus === 'persoon' ? (cl.toegewezen_aan_naam || cl.ingevuld_door_naam || '-') : <span className="text-slate-300">Locatie-breed</span>}
                  </td>
                  <td className="py-3 px-4 text-slate-500 text-xs">{cl.ingevuld_door_naam || '-'}</td>
                  <td className="py-3 px-4">
                    {cl.score?.percentage != null
                      ? <Badge className={scoreBg(cl.score.percentage)}>{cl.score.percentage}%</Badge>
                      : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="py-3 px-4 text-slate-500">{fmtDateShort(cl.ingevuld_op)}</td>
                  <td className="py-3 px-2"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.length < totaal && (
          <div className="p-3 text-center border-t border-slate-100">
            <Button variant="outline" size="sm" disabled={meerBezig}
              onClick={() => haal(data.length, true)} data-testid="ingevuld-meer-laden">
              {meerBezig ? 'Laden...' : `Meer laden (${totaal - data.length} resterend)`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

import { TabOpschonen } from './ChecklistsOpschonen';

// ── Main Page ───────────────────────────────────────────────────────────────
const TABS = [
  { key: 'overzicht', label: 'Overzicht', icon: ClipboardList },
  { key: 'ingevuld', label: 'Ingevuld', icon: CheckCircle2 },
  { key: 'verlopen', label: 'Verlopen', icon: XCircle },
  { key: 'geescaleerd', label: 'Geëscaleerd', icon: AlertTriangle },
  { key: 'scores', label: 'Scores', icon: BarChart3 },
  { key: 'tijden', label: 'Tijden', icon: Timer },
  { key: 'opschonen', label: 'Opschonen', icon: Sparkles, alleenAdmin: true },
];

const ChecklistsOverzicht = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('overzicht');
  const [loading, setLoading] = useState(true);
  const [overzichtData, setOverzichtData] = useState(null);
  const [verlopenData, setVerlopenData] = useState(null);
  const [geescaleerdData, setGeescaleerdData] = useState(null);
  const [scoresData, setScoresData] = useState(null);
  const [tijdenData, setTijdenData] = useState(null);
  const [aiModal, setAiModal] = useState(null);
  const [verlopenCount, setVerlopenCount] = useState(0);
  const [geescaleerdCount, setGeescaleerdCount] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const fetchTab = useCallback(async (t) => {
    setLoading(true);
    try {
      const endpoints = {
        overzicht: '/checklists/management/overzicht',
        verlopen: '/checklists/management/verlopen',
        geescaleerd: '/checklists/management/geescaleerd',
        scores: '/checklists/management/scores',
        tijden: '/checklists/management/tijden',
      };
      if (!endpoints[t]) { setLoading(false); return; }
      const res = await fetch(`${API_V2}${endpoints[t]}`, { headers: hdrs() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (t === 'overzicht') setOverzichtData(data);
      if (t === 'verlopen') { setVerlopenData(data); setVerlopenCount(data.length); }
      if (t === 'geescaleerd') { setGeescaleerdData(data); setGeescaleerdCount(data.length); }
      if (t === 'scores') setScoresData(data);
      if (t === 'tijden') setTijdenData(data);
    } catch { toast.error('Data laden mislukt'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchTab('overzicht');
    fetch(`${API_V2}/checklists/management/verlopen`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : []).then(d => setVerlopenCount(d.length)).catch(() => {});
    fetch(`${API_V2}/checklists/management/geescaleerd`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : []).then(d => setGeescaleerdCount(d.length)).catch(() => {});
  }, [fetchTab]);

  const switchTab = (t) => {
    setTab(t);
    if (t === 'ingevuld') { setLoading(false); return; } // TabIngevuld fetches itself
    const dataMap = { overzicht: overzichtData, verlopen: verlopenData, geescaleerd: geescaleerdData, scores: scoresData, tijden: tijdenData };
    if (!dataMap[t]) fetchTab(t);
  };

  const openAi = (checklistId, locatieNaam) => setAiModal({ checklistId, locatieNaam });

  const handleDeleteChecklist = async () => {
    if (!deleting) return;
    try {
      const res = await fetch(`${API_V2}/checklists/${deleting.id}`, { method: 'DELETE', headers: hdrs() });
      if (res.ok) {
        toast.success('Checklist verwijderd');
        fetchTab(tab);
        if (tab !== 'verlopen') fetch(`${API_V2}/checklists/management/verlopen`, { headers: hdrs() }).then(r => r.ok ? r.json() : []).then(d => { setVerlopenData(d); setVerlopenCount(d.length); }).catch(() => {});
        if (tab !== 'geescaleerd') fetch(`${API_V2}/checklists/management/geescaleerd`, { headers: hdrs() }).then(r => r.ok ? r.json() : []).then(d => { setGeescaleerdData(d); setGeescaleerdCount(d.length); }).catch(() => {});
      } else toast.error('Verwijderen mislukt');
    } catch { toast.error('Verwijderen mislukt'); }
    finally { setDeleting(null); }
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleting?.length) return;
    try {
      const res = await fetch(`${API_V2}/checklists/bulk-delete`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify({ ids: bulkDeleting }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message || `${d.deleted} checklists verwijderd`);
        fetchTab(tab);
      } else toast.error('Bulk verwijderen mislukt');
    } catch { toast.error('Bulk verwijderen mislukt'); }
    finally { setBulkDeleting(null); }
  };

  // R2-a: na het uitvoeren van de opschoning zijn de tabbadges en de
  // overzichtstotalen verouderd — direct opnieuw ophalen (geen reload nodig).
  const herlaadNaOpschonen = useCallback(async () => {
    const haal = async (pad) => {
      const res = await fetch(`${API_V2}${pad}`, { headers: hdrs() });
      return res.ok ? res.json() : [];
    };
    try {
      const [over, verl, geesc] = await Promise.all([
        haal('/checklists/management/overzicht'),
        haal('/checklists/management/verlopen'),
        haal('/checklists/management/geescaleerd'),
      ]);
      setOverzichtData(over);
      setVerlopenData(verl); setVerlopenCount(verl.length);
      setGeescaleerdData(geesc); setGeescaleerdCount(geesc.length);
      setScoresData(null); setTijdenData(null);
    } catch { toast.error('Vernieuwen mislukt'); }
  }, []);

  const handleViewDetail = (id) => { setDetailId(id); };  const handleBackFromDetail = () => { setDetailId(null); };

  // If viewing detail: render a simple detail viewer
  if (detailId) {
    return <ManagementDetailView checklistId={detailId} onBack={handleBackFromDetail} />;
  }

  return (
    <div className="space-y-5 max-w-6xl mx-auto px-4 sm:px-0" data-testid="checklists-management-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="management-title">
          <ClipboardList className="w-6 h-6" /> Checklists Management
        </h1>
        <p className="text-sm text-slate-500">Overzicht van alle locaties</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto" data-testid="management-tabs">
        {TABS.filter(t => !t.alleenAdmin || user?.rol === 'admin').map(t => (
          <button key={t.key} onClick={() => switchTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
              tab === t.key ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`} data-testid={`tab-${t.key}`}>
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.key === 'verlopen' && verlopenCount > 0 && (
              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 ml-0.5">{verlopenCount}</Badge>
            )}
            {t.key === 'geescaleerd' && geescaleerdCount > 0 && (
              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 ml-0.5">{geescaleerdCount}</Badge>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" />
        </div>
      ) : (
        <>
          {tab === 'overzicht' && <TabOverzicht data={overzichtData} onAi={openAi} />}
          {tab === 'ingevuld' && <TabIngevuld onViewDetail={handleViewDetail} />}
          {tab === 'verlopen' && <TabVerlopen data={verlopenData} onDelete={setDeleting} onBulkDelete={setBulkDeleting} />}
          {tab === 'geescaleerd' && <TabGeescaleerd data={geescaleerdData} onDelete={setDeleting} onBulkDelete={setBulkDeleting} />}
          {tab === 'scores' && <TabScores data={scoresData} />}
          {tab === 'tijden' && <TabTijden data={tijdenData} />}
          {tab === 'opschonen' && user?.rol === 'admin' && (
            <TabOpschonen onUitgevoerd={herlaadNaOpschonen} />
          )}
        </>
      )}

      <AiModal open={!!aiModal} onClose={() => setAiModal(null)}
        checklistId={aiModal?.checklistId} locatieNaam={aiModal?.locatieNaam} />

      {/* Single delete dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Checklist verwijderen?</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je deze checklist wilt verwijderen? "{deleting?.template_naam}" — {deleting?.locatie_naam}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Annuleren</Button>
            <Button onClick={handleDeleteChecklist} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-checklist-btn">Verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete dialog */}
      <Dialog open={!!bulkDeleting} onOpenChange={() => setBulkDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Bulk verwijderen?</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je {bulkDeleting?.length || 0} checklists wilt verwijderen? Dit kan niet ongedaan worden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleting(null)}>Annuleren</Button>
            <Button onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-bulk-delete-btn">
              Verwijder {bulkDeleting?.length || 0} checklists
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistsOverzicht;
